import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IImage } from '../../../../models/response/InspectionResponse';
import { DefaultCarSvg, GuaranteeSvg, PlaySvg } from '../../../../svg';
import { Image, Input, Spinner, Switch } from '../../../../ui';
import { Modal } from '../../../../ui/Modal';
import { imageUrl, videoUrl } from '../../../../utils/imageUrl';
import ImageEditor from '../../../CarPictures/Components/ImageEditor';
import { RadioOptions } from '../RadioOptions';

import { ICard } from './Card';
import {
  AddImage,
  DefContainer,
  DeleteButton,
  ImageContainer,
  ImageWrapper,
  ItemCard,
  ItemHead,
  ItemTitle,
  QuestionContainer,
  QuestionWrapper,
  RowWrapper,
  SwitchBox,
  SwitchContainer,
} from './CardElements';

export const Card: React.FC<ICard.IProps> = (props) => {
  const { t } = useTranslation();
  const [showList, setShowList] = useState(false);
  const [selectedImage, setSelectedImage] = useState<IImage | null>(null);
  const [isShowEditor, onToggleIsShowEditor] = useState(false);

  const hasValue = () => {
    const { defects } = props;
    defects.map((v) => {
      if (v.has_defect) {
        setShowList(true);
      }
    });
    if (props.is_option) {
      setShowList(props.available);
    }
  };

  useEffect(() => {
    hasValue();
  }, [props]);

  return (
    <ItemCard key={props.slug}>
      {props.is_option ? (
        <Switch
          className={'tw-py-[12px] tw-px-[8px]'}
          title={props.name}
          isOption={true}
          is_guaranteed={props.is_guaranteed}
          isActive={props.available}
          onChange={() => props.onChangeOption(props.slug, !props.available)}
        />
      ) : (
        <ItemHead>
          <RowWrapper>
            <GuaranteeSvg
              className={`tw-w-[16px] tw-mr-2 ${
                (!props.is_guaranteed && 'tw-grayscale tw-opacity-50') || ''
              }`}
            />
            <ItemTitle>{props.name}</ItemTitle>
          </RowWrapper>
          <SwitchContainer>
            <SwitchBox
              className={'icon-Left-icon34'}
              buttonType={'close'}
              isChecked={showList}
              onClick={() => setShowList(true)}
            />
            <SwitchBox
              className={'icon-Check'}
              buttonType={'check'}
              isChecked={!showList}
              onClick={() => setShowList(false)}
            />
          </SwitchContainer>
        </ItemHead>
      )}
      {showList && (props.defects.length > 0 || props.is_option) && (
        <QuestionContainer>
          <Input
            label={t('car_inspection.description')}
            value={props.description || props.default_description}
            placeholder={props.default_description}
            onBlur={(v) => props.onChangeOption(props.slug, true, v.currentTarget.value)}
          />
          <QuestionWrapper>
            {props.is_single_defect ? (
              <DefContainer>
                <RadioOptions
                  defects={props.defects}
                  isLoadingPatchDefect={props.isLoadingPatchDefect}
                  defectSlug={props.defectSlug}
                  onClickAddImage={props.onClickAddImage}
                  onFocusInput={props.onFocusInput}
                  onBlurInput={props.onBlurInput}
                  onRemoveOption={props.onClickCheckBox}
                  onClickRemoveImage={props.onClickRemoveImage}
                />
              </DefContainer>
            ) : (
              props.defects.map((defItem) => (
                <DefContainer key={defItem.defect.slug}>
                  <Switch
                    title={defItem.defect.name}
                    isActive={defItem.has_defect}
                    isSmall={true}
                    onChange={() => props.onClickCheckBox(defItem)}
                    color={defItem.severity_color}
                    severityText={defItem.severity_text}
                  />
                  {defItem.has_defect && (
                    <>
                      <Input
                        label={t('car_inspection.note')}
                        value={defItem.note}
                        marginWrapper={'16px 0 0 0'}
                        onFocus={() => props.onFocusInput(defItem)}
                        onBlur={(v) => props.onBlurInput(v.currentTarget.value)}
                      />
                      <ImageContainer>
                        <AddImage
                          className={
                            props.isLoadingPatchDefect && props.defectSlug === defItem.defect.slug
                              ? ''
                              : 'icon-u_camera-plus'
                          }
                          onClick={() => props.onClickAddImage(defItem)}
                        />
                        {defItem.images?.map((image) => (
                          <ImageWrapper key={image.uuid}>
                            {props.isLoadingRemoveImage && image.uuid === props.imageUUID ? (
                              <Spinner isLoading={true} />
                            ) : (
                              <div onClick={() => setSelectedImage(image)}>
                                {image.type.includes('video') ? (
                                  <div
                                    className={
                                      'tw-relative tw-flex tw-items-center tw-justify-center'
                                    }
                                  >
                                    <DefaultCarSvg className={'tw-w-[80px] tw-h-[60px]'} />
                                    <PlaySvg className={'tw-absolute tw-w-[40px] tw-h-[40px]'} />
                                  </div>
                                ) : (
                                  <Image src={image.url} height={128} width={128} />
                                )}
                                <DeleteButton
                                  className={'icon-u_trash-alt'}
                                  onClick={(e) => {
                                    props.onClickRemoveImage(image.uuid);
                                    e.stopPropagation();
                                  }}
                                />
                              </div>
                            )}
                          </ImageWrapper>
                        ))}
                      </ImageContainer>
                    </>
                  )}
                </DefContainer>
              ))
            )}
          </QuestionWrapper>
        </QuestionContainer>
      )}
      <Modal
        componentLeft={
          <button
            onClick={() => onToggleIsShowEditor(true)}
            className="tw-p-3 tw-rounded-lg tw-bg-gray700 tw-text-gray0"
          >
            {t('image_picker.customize')}
          </button>
        }
        isShow={!!selectedImage}
        title={''}
        onCloseModal={() => setSelectedImage(null)}
      >
        <div className={'tw-mt-6 tw-max-h-[60vh]'}>
          {!!selectedImage &&
            (selectedImage.type.includes('video') ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                controls
                src={videoUrl(selectedImage.url)}
                className={'tw-rounded-[8px] tw-w-full tw-max-h-[55vh] tw-mb-[40px]'}
              />
            ) : (
              <Image
                src={selectedImage.url}
                height={300}
                width={300}
                className={'tw-rounded-[8px] tw-mb-[40px]'}
              />
            ))}
        </div>
        {selectedImage?.url && isShowEditor && (
          <ImageEditor
            image={imageUrl({ src: selectedImage?.url, height: 900, width: 1200 })}
            onCloseModal={() => onToggleIsShowEditor(false)}
            onEditedImage={(image) => {
              props.onChooseImage(image, undefined, props.slug, props.defectSlug);
              selectedImage?.uuid && props.onClickRemoveImage(selectedImage.uuid, true);
              setSelectedImage(null);
              onToggleIsShowEditor(false);
            }}
          />
        )}
      </Modal>
    </ItemCard>
  );
};
